import { ReactNode, useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl';
  variant?: 'default' | 'danger';
  /** `window` = neutral OS-style title bar and frame; `app` = branded header (default). */
  chrome?: 'app' | 'window';
}

const SIZE = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  '2xl': 'max-w-5xl',
};

const FOCUSABLE = 'a[href],button:not([disabled]),textarea,input,select,[tabindex]:not([tabindex="-1"])';

export function Modal({
  open, onClose, title, subtitle, icon, children,
  size = 'md', variant = 'default', chrome = 'app',
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { onClose(); return; }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
      if (!focusable.length) return;
      const first = focusable[0]!;
      const last = focusable[focusable.length - 1]!;
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    };
    if (open) document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Auto-focus first focusable element when modal opens
  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      const first = panel.querySelector<HTMLElement>(FOCUSABLE);
      first?.focus();
    }, 50);
  }, [open]);

  if (!open) return null;

  const isWindow = chrome === 'window';

  return (
    <div
      className={
        isWindow
          ? 'fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/35'
          : 'fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/50 backdrop-blur-md'
      }
      onClick={onClose}
    >
      <div
        ref={panelRef}
        className={
          isWindow
            ? `relative w-full ${SIZE[size]} rounded-lg border border-gray-400 bg-[#f5f5f5] shadow-[0_4px_24px_rgba(0,0,0,0.28)] dark:border-gray-600 dark:bg-[#2b2b2b]`
            : `relative w-full ${SIZE[size]} vscode-bg-elevated border vscode-border rounded-xl shadow-2xl`
        }
        onClick={(e) => e.stopPropagation()}
        role={variant === 'danger' ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby="modal-title"
      >

        <div
          className={
            isWindow
              ? 'flex items-center justify-between rounded-t-lg border-b border-gray-300 bg-[#ececec] px-3 py-2 dark:border-gray-600 dark:bg-[#3c3c3c]'
              : 'flex items-center justify-between rounded-t-xl border-b vscode-border px-5 py-3.5'
          }
          style={isWindow ? undefined : { backgroundColor: 'var(--brand-popup-header)' }}
        >
          <div className="flex min-w-0 items-center gap-2">
            {icon && (
              <span
                className={
                  isWindow
                    ? 'flex h-6 w-6 flex-shrink-0 items-center justify-center text-gray-700 dark:text-gray-200 [&_svg]:h-4 [&_svg]:w-4'
                    : 'flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-md bg-white/15 [&_svg]:h-4 [&_svg]:w-4'
                }
              >
                <span className={isWindow ? '' : 'text-white'}>{icon}</span>
              </span>
            )}
            <div className="min-w-0">
              <h2
                id="modal-title"
                className={
                  isWindow
                    ? 'truncate text-sm font-semibold leading-tight text-gray-900 dark:text-gray-100'
                    : 'truncate text-sm font-semibold leading-tight text-white'
                }
              >
                {title}
              </h2>
              {subtitle && (
                <p
                  className={
                    isWindow
                      ? 'mt-0.5 truncate text-[11px] leading-tight text-gray-600 dark:text-gray-400'
                      : 'mt-0.5 truncate text-[11px] leading-tight text-white/65'
                  }
                >
                  {subtitle}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={
              isWindow
                ? 'ml-2 flex-shrink-0 rounded p-1 text-gray-600 transition-colors hover:bg-gray-300 dark:text-gray-300 dark:hover:bg-gray-600'
                : 'ml-2 flex-shrink-0 rounded-md p-1 text-white/70 transition-colors hover:bg-white/15 hover:text-white'
            }
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div
          className={
            isWindow
              ? 'border-t border-gray-200 bg-white p-5 dark:border-gray-600 dark:bg-[#1e1e1e]'
              : 'p-5'
          }
        >
          {children}
        </div>
      </div>
    </div>
  );
}
